/**
 * extract-text.js — pull plain text out of an uploaded document so it can be used
 * as a recording transcript (the same path .txt already uses). Runs in the browser;
 * the heavy parsers (pdf.js) are lazy-loaded so they don't bloat the main bundle.
 *
 * Supported: .txt, .docx, .pdf
 */

/** Extensions we can turn into a transcript on the client. */
export function isExtractableDoc(filename) {
  const ext = (filename || '').toLowerCase().split('.').pop();
  return ext === 'txt' || ext === 'docx' || ext === 'pdf';
}

/** Extract the text of a document File. Returns a trimmed string ('' if none). */
export async function extractDocumentText(file) {
  const ext = (file?.name || '').toLowerCase().split('.').pop();
  if (ext === 'txt') return (await file.text()).trim();
  if (ext === 'docx') return extractDocx(file);
  if (ext === 'pdf') return extractPdf(file);
  return '';
}

// A .docx is a ZIP; the body text lives in word/document.xml as <w:t> nodes inside
// <w:p> paragraphs. We unzip with fflate (tiny) and strip the XML to text.
async function extractDocx(file) {
  const { unzipSync, strFromU8 } = await import('fflate');
  const entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const doc = entries['word/document.xml'];
  if (!doc) return '';
  return strFromU8(doc)
    .replace(/<w:tab[^>]*\/?>/g, '\t')   // tabs
    .replace(/<w:br[^>]*\/?>/g, '\n')    // line breaks
    .replace(/<\/w:p>/g, '\n')           // paragraph ends → newline
    .replace(/<[^>]+>/g, '')             // drop all remaining tags (text stays)
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

let _pdfWorkerReady = false;

async function extractPdf(file) {
  const pdfjs = await import('pdfjs-dist');
  if (!_pdfWorkerReady) {
    pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
    _pdfWorkerReady = true;
  }
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  let out = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const content = await (await pdf.getPage(i)).getTextContent();
    out += content.items.map((it) => (it && 'str' in it ? it.str : '')).join(' ') + '\n';
  }
  return out.trim();
}
